import { type ChangeEvent, Fragment, memo } from "react";
import "Components/LiveLayout/LiveToolbar/LiveToolbar.less";
import { useAppDispatch, useAppSelector } from "Data/Redux/Store";
import { selectAudioPlayback, selectAudioTransmission, selectBoundingBoxEnabled, selectDateTimeEnabled,
	selectEventIconsEnabled, selectFocus, selectIdEnabled, selectResolutionEnabled, selectRulesEnabled,
	selectStreamIndex,
	selectTrajectoriesEnabled, selectZoom, setAudioPlaybackAction, setAudioTransmissionAction,
	setBoundingBoxEnabledAction, setDateTimeEnabledAction, setEventIconsEnabledAction, setFocusAction,
	setIdEnabledAction, setResolutionEnabledAction, setRulesEnabledAction, setStreamIndexAction,
	setTrajectoriesEnabledAction, setZoomAction } from "Data/Redux/Slices/LiveView";
import {
	Button, ButtonTypes, Checkbox, Icon, IconSize, IconWeight, Input, Modal, Popover, PopoverTrigger, RangeSlider,
	Select
} from "@clintonelec/react-storybook";
import type { UnknownAction } from "redux";
import { selectStreams } from "Data/Redux/Slices/Settings/Camera/Codec";
import StreamStatusModal from "Components/LiveLayout/StreamStatusModal";

function LiveToolbar() {
	const dispatch = useAppDispatch();
	// region Redux State Selection
	const audioTransmissionEnabled = useAppSelector(selectAudioTransmission);
	const audioPlaybackEnabled = useAppSelector(selectAudioPlayback);
	const zoom = useAppSelector(selectZoom);
	const focus = useAppSelector(selectFocus);
	const dateTimeEnabled = useAppSelector(selectDateTimeEnabled);
	const resolutionEnabled = useAppSelector(selectResolutionEnabled);
	const eventIconsEnabled = useAppSelector(selectEventIconsEnabled);
	const rulesEnabled = useAppSelector(selectRulesEnabled);
	const boundingBoxEnabled = useAppSelector(selectBoundingBoxEnabled);
	const trajectoriesEnabled = useAppSelector(selectTrajectoriesEnabled);
	const idEnabled = useAppSelector(selectIdEnabled);
	const videoStreams = useAppSelector(selectStreams);
	const streamIndex = useAppSelector(selectStreamIndex);
	// endregion
	// region Redux Dispatch Bindings
	const handleTransmissionEnabledClick = () => dispatch(setAudioTransmissionAction(!audioTransmissionEnabled));
	const handlePlaybackEnabledClick = () => dispatch(setAudioPlaybackAction(!audioPlaybackEnabled));

	const handleZoomChange = (newZoom: number | number[]) => {
		if (Array.isArray(newZoom)) {
			return;
		}

		dispatch(setZoomAction(newZoom));
	};

	const handleDecrementZoomClick = () => dispatch(setZoomAction(zoom - 1));
	const handleIncrementZoomClick = () => dispatch(setZoomAction(zoom + 1));

	const handleFocusChange = (newFocus: number | number[]) => {
		if (Array.isArray(newFocus)) {
			return;
		}

		dispatch(setFocusAction(newFocus));
	};

	const handleDecrementFocusClick = () => dispatch(setFocusAction(focus - 1));
	const handleIncrementFocusClick = () => dispatch(setFocusAction(focus + 1));
	const createCheckboxHandler = (actionCreator: (actionValue: boolean) => UnknownAction) =>
		(checkboxEvent: ChangeEvent<HTMLInputElement>) =>
			dispatch(actionCreator(!!checkboxEvent?.currentTarget?.checked));

	const handleDateTimeEnabledChange = createCheckboxHandler(setDateTimeEnabledAction);
	const handleResolutionEnabledChange = createCheckboxHandler(setResolutionEnabledAction);
	const handleEventIconsEnabledChange = createCheckboxHandler(setEventIconsEnabledAction);
	const handleRulesEnabledChange = createCheckboxHandler(setRulesEnabledAction);
	const handleBoundingBoxEnabledChange = createCheckboxHandler(setBoundingBoxEnabledAction);
	const handleTrajectoriesEnabledChange = createCheckboxHandler(setTrajectoriesEnabledAction);
	const handleIdEnabledChange = createCheckboxHandler(setIdEnabledAction);

	const handleStreamIndexSelect = (newStreamIndex: string | null) => {
		if (!newStreamIndex) {
			return;
		}

		dispatch(setStreamIndexAction(+newStreamIndex));
	};

	// endregion
	const audioPlaybackIcon = audioPlaybackEnabled ? "volume" : "volume-slash";
	const audioTransmissionIcon = audioTransmissionEnabled ? "microphone" : "microphone-slash";
	const videoStreamOptions = videoStreams
		.map((videoStream, index) => {
			return {
				label: `Stream ${ index + 1 }: ${ videoStream.resolution } @ ${ videoStream.frameRate }fps`,
				value: index
			};
		});

	const liveViewOptions = (
		<Fragment>
			<Checkbox onChange={ handleDateTimeEnabledChange } checked={ dateTimeEnabled }>
				Date/Time
			</Checkbox>
			<Checkbox onChange={ handleResolutionEnabledChange } checked={ resolutionEnabled }>
				Resolution
			</Checkbox>
			<Checkbox onChange={ handleEventIconsEnabledChange } checked={ eventIconsEnabled }>
				Event Icons
			</Checkbox>
		</Fragment>
	);

	const aiDisplayOptions = (
		<Fragment>
			<Checkbox onChange={ handleRulesEnabledChange } checked={ rulesEnabled }>Display Rules</Checkbox>
			<Checkbox onChange={ handleBoundingBoxEnabledChange } checked={ boundingBoxEnabled }>
				Display Object Bounding Box
			</Checkbox>
			<Checkbox onChange={ handleTrajectoriesEnabledChange } checked={ trajectoriesEnabled }>
				Display Object Trajectories
			</Checkbox>
			<Checkbox onChange={ handleIdEnabledChange } checked={ idEnabled }>
				Display Object ID
			</Checkbox>
		</Fragment>
	);

	const lensAdjustmentOptions = (
		<Fragment>
			<div className="lens-adjustment-row">
				<span>Zoom</span>
				<Button
					type={ ButtonTypes.SECONDARY }
					onClick={ handleDecrementZoomClick }
					icon={ { name: "minus" } }
				/>
				<RangeSlider
					max={ 100 }
					min={ 0 }
					onChange={ handleZoomChange }
					value={ zoom }
				/>
				<Button
					type={ ButtonTypes.SECONDARY }
					onClick={ handleIncrementZoomClick }
					icon={ { name: "plus" } }
				/>
				<Input value={ zoom } />
			</div>
			<div className="lens-adjustment-row">
				<span>Focus</span>
				<Button
					type={ ButtonTypes.SECONDARY }
					onClick={ handleDecrementFocusClick }
					icon={ { name: "minus" } }
				/>
				<RangeSlider
					max={ 100 }
					min={ 0 }
					onChange={ handleFocusChange }
					value={ focus }
				/>
				<Button
					type={ ButtonTypes.SECONDARY }
					onClick={ handleIncrementFocusClick }
					icon={ { name: "plus" } }
				/>
				<Input value={ focus } />
			</div>
			<div className="lens-adjustment-buttons">
				<Button type={ ButtonTypes.SECONDARY } ghost>
					REFOCUS
				</Button>
				<Button type={ ButtonTypes.SECONDARY } ghost>
					LENS RESET
				</Button>
			</div>
		</Fragment>
	);

	return (
		<div className="live-toolbar">
			<div className="live-stream-select">
				<Select
					allowClear={ false }
					menuPlacement="top"
					options={ videoStreamOptions }
					value={ streamIndex }
					onSelect={ handleStreamIndexSelect }
				/>
			</div>
			<div className="live-toolbar-buttons">
				<div className="live-toolbar-button">
					<Icon
						fixedWidth
						iconWeight={ IconWeight.REGULAR }
						name="camera"
						size={ IconSize.SMALLER }
					/>
				</div>
				<div className="live-toolbar-button" onClick={ handlePlaybackEnabledClick }>
					<Icon
						fixedWidth
						iconWeight={ IconWeight.REGULAR }
						name={ audioPlaybackIcon }
						size={ IconSize.SMALLER }
					/>
				</div>
				<div className="live-toolbar-button" onClick={ handleTransmissionEnabledClick }>
					<Icon
						fixedWidth
						iconWeight={ IconWeight.REGULAR }
						name={ audioTransmissionIcon }
						size={ IconSize.SMALLER }
					/>
				</div>
				<div className="live-toolbar-button">
					<Icon
						fixedWidth
						iconWeight={ IconWeight.REGULAR }
						name="siren-on"
						size={ IconSize.SMALLER }
					/>
				</div>
				<div className="live-toolbar-button">
					<Icon
						fixedWidth
						iconWeight={ IconWeight.REGULAR }
						name="bullhorn"
						size={ IconSize.SMALLER }
					/>
				</div>
				<div className="live-toolbar-button">
					<Icon
						iconWeight={ IconWeight.REGULAR }
						name="expand"
						size={ IconSize.SMALLER }
					/>
				</div>
				<Modal
					title="AI Display Options"
					modalContent={ aiDisplayOptions }>
					<div className="live-toolbar-button">
						<Icon
							iconWeight={ IconWeight.REGULAR }
							name="microchip-ai"
							size={ IconSize.SMALLER }
						/>
					</div>
				</Modal>
				<Modal
					title="Live View Display Options"
					modalContent={ liveViewOptions }>
					<div className="live-toolbar-button">
						<Icon
							iconWeight={ IconWeight.REGULAR }
							name="desktop"
							size={ IconSize.SMALLER }
						/>
					</div>
				</Modal>
				<div className="live-toolbar-button">
					<Popover
						title="Lens Adjustment"
						arrow={ false }
						className="lens-adjustment-popover"
						content={ lensAdjustmentOptions }
						trigger={ PopoverTrigger.CLICK }
					>
						<Icon
							iconWeight={ IconWeight.REGULAR }
							name="aperture"
							size={ IconSize.SMALLER }
						/>
					</Popover>
				</div>
				<StreamStatusModal>
					<div className="live-toolbar-button">
						<Icon
							iconWeight={ IconWeight.REGULAR }
							name="square-info"
							size={ IconSize.SMALLER }
						/>
					</div>
				</StreamStatusModal>
			</div>
		</div>
	);
}

export default memo(LiveToolbar);
